import {
  GET_INVITEES,
  GET_MEETING,
  GET_MEETINGS,
  INVITEE_TAG_TYPE,
  MEETING_TAG_TYPE
} from './constants'
import { meetingsGraphqlApi } from './graphql_api'
import type { GraphQLEdge, InviteeListResult, Meeting, MeetingDay, MeetingListResult, MeetingResult, MeetingsGraphQLResult } from './types'

const GRAPHQL_GET_MEETINGS = 'getMeetings'
/** Supergraph list — same `meetings.edges` cache shape as legacy GraphQL `getMeetings`. */
const GRAPHQL_GET_MEETINGS_SUPERGRAPH = 'getMeetingsSupergraph'
const GRAPHQL_GET_MEETING = 'getMeeting'
/** Supergraph right rail — `getMeetingById` in `supergraphql_api.ts`. */
const GRAPHQL_GET_MEETING_SUPERGRAPH = 'getMeetingSupergraph'

const isGraphqlMeetingsListEndpoint = (endpointName: string) => (
  endpointName === GRAPHQL_GET_MEETINGS || endpointName === GRAPHQL_GET_MEETINGS_SUPERGRAPH
)

/** Single cached meeting node — same draft patch for search `getMeeting` and Supergraph detail queries. */
const isGraphqlSingleMeetingDetailEndpoint = (endpointName: string) => (
  endpointName === GRAPHQL_GET_MEETING
  || endpointName === GRAPHQL_GET_MEETING_SUPERGRAPH
)

/** Match meeting by eventId (uuid or id) for GraphQL node */
const nodeMatchesEvent = (node: { uuid?: string; id?: string | number }, eventId: string | number) => (
  node && (String(node.uuid) === String(eventId) || (node.id != null && String(node.id) === String(eventId)))
)

/** Invitee shape returned by no_shows API (camelCase after root-api transform) */
interface NoShowResponseInvitee {
  id?: string | number
  uuid?: string
  noShow?: boolean
  no_show?: boolean
  [key: string]: unknown
}

/** Normalize noShow from API response (handles both camelCase and snake_case) */
const getNoShowFromResponse = (inv: NoShowResponseInvitee): boolean => (
  inv.noShow ?? inv.no_show ?? false
)

/** Match cached invitee by id or uuid */
const inviteeMatches = (cached: { id?: string | number; uuid?: string }, responseInvitee: NoShowResponseInvitee) => (
  (responseInvitee.id != null && String(cached.id) === String(responseInvitee.id))
  || (responseInvitee.uuid != null && cached.uuid != null && String(cached.uuid) === String(responseInvitee.uuid))
)

type MeetingWithNoShow = { invitee?: { noShow?: boolean }; invitees?: Array<{ noShow?: boolean }> }

/** Count invitees with noShow in a meeting (invitee or invitees array). Used for REST cache meeting-level fields. */
function getNoShowCount(m: MeetingWithNoShow): number {
  let list: Array<{ noShow?: boolean }> = []
  if (m.invitees?.length) list = m.invitees
  else if (m.invitee) list = [m.invitee]
  return list.filter((i) => Boolean(i.noShow)).length
}

/**
 * Applies the no_shows API response invitee to REST and GraphQL caches so the UI
 * updates immediately without waiting for polling. Use after queryFulfilled.
 */
export const applyNoShowResponseToCache = ({
  api,
  state,
  dispatch,
  eventId,
  eventUuid,
  responseInvitee
}: {
  api: {
    util: {
      selectInvalidatedBy: (state: unknown, tags: string[]) => Array<{ endpointName: string; originalArgs: unknown }>
      selectCachedArgsForQuery?: (state: unknown, endpointName: string) => unknown[]
      updateQueryData: (
        endpointName: string,
        originalArgs: unknown,
        updateFn: (draft: unknown) => void
      ) => { undo: () => void }
    }
  }
  state: unknown
  dispatch: (action: unknown) => void
  eventId: string | number
  eventUuid?: string
  responseInvitee: NoShowResponseInvitee
}) => {
  if (!responseInvitee || !api?.util?.updateQueryData) return
  const value = getNoShowFromResponse(responseInvitee)
  const graphqlEventKey = eventUuid ?? eventId

  const entriesToUpdate = api.util.selectInvalidatedBy(state, [MEETING_TAG_TYPE, INVITEE_TAG_TYPE])
  const hasEntries = entriesToUpdate.length > 0
  const argsToPatch = hasEntries ? entriesToUpdate : (() => {
    const fallback: Array<{ endpointName: string; originalArgs: unknown }> = []
    if (api.util.selectCachedArgsForQuery) {
      try {
        const meetingArgs = api.util.selectCachedArgsForQuery(state, GET_MEETING) as unknown[]
        meetingArgs?.forEach((originalArgs) => fallback.push({ endpointName: GET_MEETING, originalArgs }))
        const meetingsArgs = api.util.selectCachedArgsForQuery(state, GET_MEETINGS) as unknown[]
        meetingsArgs?.forEach((originalArgs) => fallback.push({ endpointName: GET_MEETINGS, originalArgs }))
        const inviteesArgs = api.util.selectCachedArgsForQuery(state, GET_INVITEES) as unknown[]
        inviteesArgs?.forEach((originalArgs) => fallback.push({ endpointName: GET_INVITEES, originalArgs }))
      } catch {
        // ignore
      }
    }
    return fallback
  })()

  for (const { endpointName, originalArgs } of argsToPatch) {
    switch (endpointName) {
      case GET_MEETINGS: {
        type MeetingWithInvitee = Meeting & {
          id?: string | number
          invitee?: { id?: string | number; uuid?: string; noShow?: boolean }
          invitees?: Array<{ noShow?: boolean }>
          no_show_invitees_count?: number
        }
        dispatch(api.util.updateQueryData(GET_MEETINGS, originalArgs, (draft: MeetingListResult) => {
          draft.results?.forEach((day: MeetingDay) => {
            day.events?.forEach((m: MeetingWithInvitee) => {
              if (!m || (String(m.id) !== String(eventId) && String(m.uuid) !== String(eventId))) return
              if (m.invitee && inviteeMatches(m.invitee, responseInvitee)) {
                m.invitee.noShow = value
                m.status = value ? 'NO_SHOW' : 'DEFAULT'
                m.no_show_invitees_count = getNoShowCount(m)
              }
            })
          })
        }))
        break
      }
      case GET_MEETING: {
        type DraftMeeting = MeetingResult & {
          invitee?: { id?: string | number; uuid?: string; noShow?: boolean }
          invitees?: Array<{ noShow?: boolean }>
          no_show_invitees_count?: number
        }
        dispatch(api.util.updateQueryData(GET_MEETING, originalArgs, (draft: DraftMeeting) => {
          const draftEventId = draft?.id ?? draft?.uuid
          if (draftEventId == null) return
          if (String(draftEventId) !== String(eventId) && String(draftEventId) !== String(eventUuid ?? eventId)) return
          if (draft.invitee && inviteeMatches(draft.invitee, responseInvitee)) {
            draft.invitee.noShow = value
            draft.status = value ? 'NO_SHOW' : 'DEFAULT'
            draft.no_show_invitees_count = getNoShowCount(draft)
          }
        }))
        break
      }
      case GET_INVITEES:
        dispatch(api.util.updateQueryData(GET_INVITEES, originalArgs, (draft: InviteeListResult) => {
          const invitee = draft.results?.find((i: { id?: string | number; uuid?: string }) => inviteeMatches(i, responseInvitee))
          if (invitee) {
            (invitee as { noShow?: boolean }).noShow = value
          }
        }))
        break
      default:
        break
    }
  }

  const graphqlApi = meetingsGraphqlApi as unknown as typeof api & {
    util: {
      selectInvalidatedBy: (state: unknown, tags: string[]) => Array<{ endpointName: string; originalArgs: unknown }>
      selectCachedArgsForQuery?: (state: unknown, endpointName: string) => unknown[]
      updateQueryData: (
        endpointName: string,
        originalArgs: unknown,
        updateFn: (draft: MeetingsGraphQLResult) => void
      ) => { undo: () => void }
    }
  }
  if (!graphqlApi?.util?.updateQueryData) return

  try {
    let graphqlEntries = graphqlApi.util.selectInvalidatedBy(state, [MEETING_TAG_TYPE, INVITEE_TAG_TYPE])
    if (graphqlEntries.length === 0 && graphqlApi.util.selectCachedArgsForQuery) {
      graphqlEntries = []
      // Legacy Search API (`/api/search`) list + detail first — same patch order as before
      // Supergraph endpoints were added; keep legacy `getMeeting` / `getMeetings` ahead so
      // behavior matches the original fallback when only Search API queries are cached.
      const gqlMeetingArgs = graphqlApi.util.selectCachedArgsForQuery(state, GRAPHQL_GET_MEETING) as unknown[]
      gqlMeetingArgs?.forEach((originalArgs) => graphqlEntries.push({ endpointName: GRAPHQL_GET_MEETING, originalArgs }))
      const gqlMeetingsArgs = graphqlApi.util.selectCachedArgsForQuery(state, GRAPHQL_GET_MEETINGS) as unknown[]
      gqlMeetingsArgs?.forEach((originalArgs) => graphqlEntries.push({ endpointName: GRAPHQL_GET_MEETINGS, originalArgs }))
      const gqlMeetingSuperArgs = graphqlApi.util.selectCachedArgsForQuery(state, GRAPHQL_GET_MEETING_SUPERGRAPH) as unknown[]
      gqlMeetingSuperArgs?.forEach((originalArgs) => graphqlEntries.push({
        endpointName: GRAPHQL_GET_MEETING_SUPERGRAPH,
        originalArgs
      }))
      const gqlMeetingsSuperArgs = graphqlApi.util.selectCachedArgsForQuery(state, GRAPHQL_GET_MEETINGS_SUPERGRAPH) as unknown[]
      gqlMeetingsSuperArgs?.forEach((originalArgs) => graphqlEntries.push({
        endpointName: GRAPHQL_GET_MEETINGS_SUPERGRAPH,
        originalArgs
      }))
    }
    for (const { endpointName, originalArgs } of graphqlEntries) {
      if (isGraphqlMeetingsListEndpoint(endpointName)) {
        dispatch(graphqlApi.util.updateQueryData(endpointName, originalArgs, (draft: MeetingsGraphQLResult) => {
          draft.meetings?.edges?.forEach((edge: GraphQLEdge) => {
            const node = edge?.node
            if (!nodeMatchesEvent(node, graphqlEventKey)) return
            node.status = value ? 'NO_SHOW' : 'DEFAULT'
            if (node.invitee && inviteeMatches(node.invitee as { id?: string | number; uuid?: string }, responseInvitee)) {
              (node.invitee as { noShow?: boolean }).noShow = value
            }
          })
        }))
      } else if (isGraphqlSingleMeetingDetailEndpoint(endpointName)) {
        dispatch(graphqlApi.util.updateQueryData(endpointName, originalArgs, (draft: any) => {
          if (!draft || !nodeMatchesEvent(draft, graphqlEventKey)) return
          draft.status = value ? 'NO_SHOW' : 'DEFAULT'
          if (draft.invitee && inviteeMatches(draft.invitee, responseInvitee)) {
            draft.invitee.noShow = value
          }
        }))
      }
    }
  } catch {
    // State may not have GraphQL slice
  }
}

export const noShowOptimisticUpdate = ({
  api,
  state,
  dispatch,
  eventId,
  eventUuid,
  inviteeId,
  value
}: {
  api: {
    util: {
      selectInvalidatedBy: (state: unknown, tags: string[]) => Array<{
        endpointName: string
        originalArgs: unknown
      }>
      updateQueryData: (
        endpointName: string,
        originalArgs: unknown,
        updateFn: (draft: unknown) => void
      ) => { undo: () => void }
    }
  }
  state: unknown
  dispatch: (action: unknown) => { undo: () => void }
  eventId: string | number
  /** Meeting UUID; used for GraphQL cache matching (nodes keyed by uuid) */
  eventUuid?: string
  inviteeId: string | number
  value: boolean
}) => {
  const graphqlEventKey = eventUuid ?? eventId
  const patches: Array<{ undo: () => void }> = []

  // Guard against missing api.util in test environments
  if (!api?.util?.selectInvalidatedBy) {
    return patches
  }

  // REST API cache (getMeetings, getMeeting, getInvitees)
  for (const { endpointName, originalArgs } of api.util.selectInvalidatedBy(state, [MEETING_TAG_TYPE, INVITEE_TAG_TYPE])) {
    switch (endpointName) {
      case GET_MEETINGS:
        patches.push(
          dispatch(api.util.updateQueryData(GET_MEETINGS, originalArgs, (draft: MeetingListResult) => {
            draft.results?.forEach((day: MeetingDay) => {
              type MeetingDraft = Meeting & {
                id?: string | number
                invitee?: { noShow?: boolean }
                invitees?: Array<{ noShow?: boolean }>
                no_show_invitees_count?: number
                status?: string
              }
              const meeting = day.events?.find(
                (m: MeetingDraft) => m && m.id === eventId
              )
              if (!meeting?.invitee) return

              if (meeting.invitee) {
                meeting.invitee.noShow = value
                meeting.status = value ? 'NO_SHOW' : 'DEFAULT'
                meeting.no_show_invitees_count = getNoShowCount(meeting as MeetingWithNoShow)
              }
            })
          }))
        )
        break
      case GET_MEETING:
        type GetMeetingDraft = MeetingResult & {
          invitee?: { noShow?: boolean }
          invitees?: Array<{ noShow?: boolean }>
          no_show_invitees_count?: number
          status?: string
        }
        patches.push(
          dispatch(api.util.updateQueryData(GET_MEETING, originalArgs, (draft: GetMeetingDraft) => {
            if (draft.invitee) {
              draft.invitee.noShow = value
              draft.status = value ? 'NO_SHOW' : 'DEFAULT'
              draft.no_show_invitees_count = getNoShowCount(draft as MeetingWithNoShow)
            }
          }))
        )
        break
      case GET_INVITEES:
        patches.push(
          dispatch(api.util.updateQueryData(GET_INVITEES, originalArgs, (draft: InviteeListResult) => {
            const invitee = draft.results.find((i: { id?: string | number; noShow?: boolean }) => i.id === inviteeId)
            if (invitee) {
              invitee.noShow = value
            }
          }))
        )
        break
      default:
        break
    }
  }

  // GraphQL API cache (used by realtime list/detail when realtime features are enabled)
  const graphqlApi = meetingsGraphqlApi as unknown as typeof api & {
    util: {
      selectInvalidatedBy: (state: unknown, tags: string[]) => Array<{
        endpointName: string
        originalArgs: unknown
      }>
      updateQueryData: (
        endpointName: string,
        originalArgs: unknown,
        updateFn: (draft: MeetingsGraphQLResult) => void
      ) => { undo: () => void }
    }
  }
  if (graphqlApi?.util?.selectInvalidatedBy) {
    try {
      const graphqlEntries = graphqlApi.util.selectInvalidatedBy(state, [MEETING_TAG_TYPE, INVITEE_TAG_TYPE])
      for (const { endpointName, originalArgs } of graphqlEntries) {
        if (isGraphqlMeetingsListEndpoint(endpointName)) {
          patches.push(
            dispatch(graphqlApi.util.updateQueryData(endpointName, originalArgs, (draft: MeetingsGraphQLResult) => {
              draft.meetings?.edges?.forEach((edge: GraphQLEdge) => {
                const node = edge?.node
                if (!nodeMatchesEvent(node, graphqlEventKey)) return
                node.status = value ? 'NO_SHOW' : 'DEFAULT'
                if (node.invitee) {
                  node.invitee.noShow = value
                }
              })
            }))
          )
        } else if (isGraphqlSingleMeetingDetailEndpoint(endpointName)) {
          patches.push(
            dispatch(graphqlApi.util.updateQueryData(endpointName, originalArgs, (draft: any) => {
              if (!draft) return
              if (!nodeMatchesEvent(draft, graphqlEventKey)) return
              draft.status = value ? 'NO_SHOW' : 'DEFAULT'
              if (draft.invitee) {
                draft.invitee.noShow = value
              }
            }))
          )
        }
      }
    } catch {
      // State may not have GraphQL slice (e.g. in tests or when graphql API not mounted)
    }
  }

  return patches
}

export const buffersOptimisticUpdate = ({
  api,
  state,
  dispatch,
  eventId,
  membershipId,
  isBefore,
  duration
}: {
  api: {
    util: {
      selectInvalidatedBy: (state: unknown, tags: string[]) => Array<{
        endpointName: string
        originalArgs: unknown
      }>
      updateQueryData: (
        endpointName: string,
        originalArgs: unknown,
        updateFn: (draft: unknown) => void
      ) => { undo: () => void }
    }
  }
  state: unknown
  dispatch: (action: unknown) => { undo: () => void }
  eventId: string | number
  membershipId: string | number
  isBefore: boolean
  duration: string | number
}) => {
  const patches: Array<{ undo: () => void }> = []

  // Guard against missing api.util in test environments
  if (!api?.util?.selectInvalidatedBy) {
    return patches
  }

  for (const { endpointName, originalArgs } of api.util.selectInvalidatedBy(state, [MEETING_TAG_TYPE])) {
    if (endpointName === GET_MEETINGS) {
      patches.push(
        dispatch(api.util.updateQueryData(GET_MEETINGS, originalArgs, (draft: MeetingListResult) => {
          draft.results?.forEach((day: MeetingDay) => {
            const meeting = day.events?.find((m: Meeting & {
              id?: string | number
              eventMemberships?: Array<{
                id?: string | number
                beforeBufferTime?: string | number
                afterBufferTime?: string | number
                hasBeforeBuffer?: boolean
                hasAfterBuffer?: boolean
              }>
            }) => m.id === eventId)
            if (!meeting) return

            const membership = meeting.eventMemberships?.find((m) => m.id === membershipId)
            if (!membership) return

            membership[isBefore ? 'beforeBufferTime' : 'afterBufferTime'] = duration
            membership[isBefore ? 'hasBeforeBuffer' : 'hasAfterBuffer'] = Number(duration) > 0
          })
        }))
      )
    }
  }

  return patches
}

export const meetingNotesOptimisticUpdate = ({
  api,
  state,
  dispatch,
  eventId,
  internalNotes
}: {
  api: {
    util: {
      selectInvalidatedBy: (state: unknown, tags: string[]) => Array<{
        endpointName: string
        originalArgs: unknown
      }>
      updateQueryData: (
        endpointName: string,
        originalArgs: unknown,
        updateFn: (draft: unknown) => void
      ) => { undo: () => void }
    }
  }
  state: unknown
  dispatch: (action: unknown) => { undo: () => void }
  eventId: string | number
  internalNotes: string
}) => {
  const patches: Array<{ undo: () => void }> = []

  // Guard against missing api.util in test environments
  if (!api?.util?.selectInvalidatedBy) {
    return patches
  }

  // Use selectInvalidatedBy to find all queries that provide Meeting tag
  // This should find all relevant cached queries
  for (const { endpointName, originalArgs } of api.util.selectInvalidatedBy(state, [MEETING_TAG_TYPE])) {
    switch (endpointName) {
      case GET_MEETINGS:
        patches.push(
          dispatch(api.util.updateQueryData(GET_MEETINGS, originalArgs, (draft: MeetingListResult) => {
            if (!draft?.results) return
            draft.results.forEach((day: MeetingDay) => {
              if (!day?.events) return
              const meeting = day.events.find((m: Meeting & {
                id?: string | number
                uuid?: string | number
                internalNotes?: string
              }) => (
                m && (String(m.id) === String(eventId) || String(m.uuid) === String(eventId))
              ))
              if (meeting) {
                meeting.internalNotes = internalNotes
              }
            })
          }))
        )
        break
      case GET_MEETING:
        patches.push(
          dispatch(api.util.updateQueryData(GET_MEETING, originalArgs, (draft: MeetingResult) => {
            // Only update if this query result is for the same eventId
            // Check both id and uuid to handle different query scenarios
            const draftEventId = draft?.id || draft?.uuid
            if (draftEventId && String(draftEventId) === String(eventId)) {
              draft.internalNotes = internalNotes
            }
          }))
        )
        break
      default:
        break
    }
  }

  return patches
}

export const blockOptimisticUpdate = ({
  api,
  state,
  dispatch,
  eventId,
  inviteeId,
  blockedByHost,
  blockUuid
}: {
  api: {
    util: {
      selectInvalidatedBy: (state: unknown, tags: string[]) => Array<{
        endpointName: string
        originalArgs: unknown
      }>
      updateQueryData: (
        endpointName: string,
        originalArgs: unknown,
        updateFn: (draft: unknown) => void
      ) => { undo: () => void }
    }
  }
  state: unknown
  dispatch: (action: unknown) => { undo: () => void }
  eventId: string | number
  inviteeId: string
  blockedByHost: boolean
  blockUuid: string | null
}) => {
  const patches: Array<{ undo: () => void }> = []

  // Guard against missing api.util in test environments
  if (!api?.util?.selectInvalidatedBy) {
    return patches
  }

  for (const { endpointName, originalArgs } of api.util.selectInvalidatedBy(state, [MEETING_TAG_TYPE, INVITEE_TAG_TYPE])) {
    switch (endpointName) {
      case GET_MEETINGS:
        patches.push(
          dispatch(api.util.updateQueryData(GET_MEETINGS, originalArgs, (draft: MeetingListResult) => {
            draft.results?.forEach((day: MeetingDay) => {
              const meeting = day.events?.find((m: Meeting & {
                id?: string | number
                invitee?: {
                  uuid?: string
                  blockedByHost?: boolean
                  blockUuid?: string | null
                }
              }) => m.id === eventId)
              if (!meeting) return

              if (meeting.invitee && meeting.invitee.uuid === inviteeId) {
                meeting.invitee.blockedByHost = blockedByHost
                meeting.invitee.blockUuid = blockUuid
              }
            })
          }))
        )
        break
      case GET_MEETING:
        patches.push(
          dispatch(api.util.updateQueryData(GET_MEETING, originalArgs, (draft: MeetingResult & {
            invitee?: {
              uuid?: string
              blockedByHost?: boolean
              blockUuid?: string | null
            }
          }) => {
            if (draft.invitee && draft.invitee.uuid === inviteeId) {
              draft.invitee.blockedByHost = blockedByHost
              draft.invitee.blockUuid = blockUuid
            }
          }))
        )
        break
      case GET_INVITEES:
        patches.push(
          dispatch(api.util.updateQueryData(GET_INVITEES, originalArgs, (draft: InviteeListResult) => {
            const invitee = draft.results.find(
              (inv: { uuid?: string; blockedByHost?: boolean; blockUuid?: string | null }) => inv.uuid === inviteeId
            )
            if (invitee) {
              invitee.blockedByHost = blockedByHost
              invitee.blockUuid = blockUuid
            }
          }))
        )
        break
      default:
        break
    }
  }

  return patches
}
